import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {
  Box,
  Chip,
  CircularProgress,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
  Popper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { MentionsPopupState } from '@provision/rich-text-editor';
import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Subject } from 'rxjs';
import { PartnerGroupRules, usePartnerGroupRules } from 'src/graphql';
import { useMentionsOptions } from 'src/graphql/entities/mentions-options';
import { useListKeyboardNavigation } from 'src/utils/hooks/use-list-keyboard-navigation';

export interface MentionPopupProps {
  updatePopupStateSubject: Subject<Partial<MentionsPopupState>>;
  onMentionKeyDownRef: MutableRefObject<((event: KeyboardEvent) => boolean) | null>;
  partnerId: number | null;
}

type MentionEntryType = 'group' | 'user';

type MentionOptionItem = {
  id: string;
  label: string;
  entity: string;
  assigneeGroupId: string | null;
  assigneeGroupName: string | null;
};

interface MentionEntry {
  key: string;
  option: MentionOptionItem;
  type: MentionEntryType;
  sectionLabel: string;
}

interface MentionSection {
  id: string;
  title: string;
  entries: MentionEntry[];
}

interface MentionPopupViewModel {
  groupEntries: MentionEntry[];
  userSections: MentionSection[];
  selectableEntries: MentionEntry[];
  selectableIndexByKey: Map<string, number>;
}

const GROUP_SECTION_TITLE = 'Mention a group';
const OTHER_USERS_SECTION_ID = 'other-users';
const OTHER_USERS_SECTION_TITLE = 'Other users';
const FOOTER_HINT_TEXT = '↑↓ Navigate • Enter Select • Esc Close';
const GROUP_ENTITY = 'ConversationAssigneeGroup';
const USER_ENTITY = 'PlatformUser';
const MODAL_WIDTH = '30rem';
const MODAL_MAX_HEIGHT = '32.5rem';
const LIST_MAX_HEIGHT = '30rem';

// Aligned with Material UI v6 guidance (Context7: `/mui/material-ui/v6_1_3`) for
// using `styled()` on repeated/complex component styling instead of large inline `sx`.
// Verified installed version in this app: `@mui/material` `^6.1.0`.
const MentionPaper = styled(Paper)({
  width: MODAL_WIDTH,
  maxWidth: 'calc(100vw - 1rem)',
  maxHeight: MODAL_MAX_HEIGHT,
  overflow: 'hidden',
  '@media (max-width: 600px)': {
    width: 'calc(100vw - 1rem)',
    maxHeight: '19.5rem',
  },
});

const MentionScrollArea = styled(Box)({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  overflowX: 'hidden',
  minHeight: '8rem',
  maxHeight: LIST_MAX_HEIGHT,
  overscrollBehavior: 'contain',
  touchAction: 'pan-y',
  '@media (max-width: 600px)': {
    maxHeight: '17rem',
  },
});

const MentionSubheader = styled(ListSubheader)(({ theme }) => ({
  lineHeight: 'normal',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 600,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper,
}));

const MentionList = styled(List)(({ theme }) => ({
  minHeight: theme.spacing(2),
}));

const MentionFooter = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  fontSize: theme.typography.pxToRem(14),
  lineHeight: 1.5,
  '@media (max-width: 600px)': {
    display: 'none',
  },
}));

const MentionEntryButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  gap: theme.spacing(0.5),
  alignItems: 'center',
  backgroundColor: isSelected ? theme.palette.action.hover : undefined,
}));

const MentionEntryIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: theme.spacing(3.75),
  color: theme.palette.text.secondary,
}));

const MentionGroupChip = styled(Chip)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
}));

const MentionLoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: theme.spacing(1),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  pointerEvents: 'none',
}));

const MentionLoadingEmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  flex: 1,
  minHeight: theme.spacing(8),
  padding: theme.spacing(2),
}));

const MentionNoMatchesState = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  flex: 1,
  minHeight: theme.spacing(8),
  padding: theme.spacing(2),
  color: theme.palette.text.secondary,
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 600,
}));

export const MentionPopup: React.FC<MentionPopupProps> = ({
  updatePopupStateSubject,
  onMentionKeyDownRef,
  partnerId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [popupState, setPopupState] = useState<MentionsPopupState>({});
  const cachedMentionOptionsRef = useRef<MentionOptionItem[]>([]);

  const virtualElement = React.useMemo(
    () => ({ getBoundingClientRect: () => popupState.anchorEl ?? new DOMRect() }),
    [popupState.anchorEl],
  );

  const handleSelect = useCallback(
    (option?: MentionOptionItem) => {
      if (!option) return;

      popupState.onSelect?.({
        id: option.id,
        label: option.label,
        entity: option.entity,
        assigneeGroupId: option.assigneeGroupId,
        assigneeGroupName: option.assigneeGroupName,
      });
      setPopupState((prev) => ({ ...prev, isOpen: false, anchorEl: null, query: '' }));
    },
    [popupState],
  );

  const { partnerGroupRules } = usePartnerGroupRules(partnerId);

  const mentionsAccess = {
    users: partnerGroupRules[PartnerGroupRules.AccessToUserMentionsInConversationMessages],
    groups: partnerGroupRules[PartnerGroupRules.AccessToGroupMentionsInConversationMessages],
  };

  const categories = Object.entries(mentionsAccess)
    .filter(([_, hasAccess]) => hasAccess)
    .map(([key]) => key);

  const { records: mentionOptions, isLoading } = useMentionsOptions({
    searchTerm: popupState.query || '',
    categories,
    partnerId,
  });

  const normalizedMentionOptions = useMemo<MentionOptionItem[]>(
    () => normalizeMentionOptions(mentionOptions as unknown[]),
    [mentionOptions],
  );
  if (!isLoading) {
    cachedMentionOptionsRef.current = normalizedMentionOptions;
  }
  const mentionOptionsForView = useMemo<MentionOptionItem[]>(
    () =>
      isLoading && !normalizedMentionOptions.length
        ? cachedMentionOptionsRef.current
        : normalizedMentionOptions,
    [isLoading, normalizedMentionOptions],
  );
  const viewModel = useMemo<MentionPopupViewModel>(
    () => buildViewModel(mentionOptionsForView),
    [mentionOptionsForView],
  );
  const hasSelectableEntries = viewModel.selectableEntries.length > 0;
  const showOverlayLoading = isLoading && hasSelectableEntries;
  const showInlineLoading = isLoading && !hasSelectableEntries;

  const {
    selectedIndex,
    setSelectedIndex,
    registerItemRef,
    handleKeyDown: handleListKeyDown,
  } = useListKeyboardNavigation({
    itemCount: viewModel.selectableEntries.length,
    wrap: false,
    onEnter: (index) => {
      handleSelect(viewModel.selectableEntries[index]?.option);
    },
    onEscape: () => {
      setPopupState((prev) => ({ ...prev, isOpen: false }));
    },
  });

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!popupState.isOpen) return false;

      return handleListKeyDown(event);
    },
    [handleListKeyDown, popupState.isOpen],
  );

  const handleClickAway = useCallback(
    () => setPopupState((prev) => ({ ...prev, isOpen: false })),
    [],
  );

  useEffect(() => {
    if (!onMentionKeyDownRef) return;
    onMentionKeyDownRef.current = handleKeyDown;

    return () => {
      onMentionKeyDownRef.current = null;
    };
  }, [handleKeyDown, onMentionKeyDownRef]);

  useEffect(() => {
    const subscription = updatePopupStateSubject.subscribe((value) => {
      setPopupState((prev) => ({ ...prev, ...value }));
    });

    return () => subscription.unsubscribe();
  }, [updatePopupStateSubject]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [viewModel.selectableEntries, setSelectedIndex]);

  const popperZIndex =
    ((theme.zIndex as { app?: { popper?: number } })?.app?.popper ?? theme.zIndex.modal) + 1;

  return (
    <Popper
      open={Boolean(popupState.isOpen)}
      anchorEl={virtualElement}
      placement="top-start"
      modifiers={[
        { name: 'offset', options: { offset: [0, isMobile ? 16 : 8] } },
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            altAxis: true,
            padding: 8,
          },
        },
        {
          name: 'flip',
          enabled: !isMobile,
          options: {
            fallbackPlacements: ['bottom-start', 'top-end', 'bottom-end'],
            padding: 8,
          },
        },
      ]}
      sx={{ zIndex: popperZIndex, fontSize: '0.85rem' }}
    >
      <ClickAwayListener onClickAway={handleClickAway}>
        <MentionPaper elevation={4}>
          <MentionScrollArea>
            <MentionList dense disablePadding>
              {viewModel.groupEntries.length ? (
                <>
                  <MentionSubheader disableSticky>{GROUP_SECTION_TITLE}</MentionSubheader>
                  {viewModel.groupEntries.map((entry) =>
                    renderMentionEntry({
                      entry,
                      selectedIndex,
                      setSelectedIndex,
                      selectableIndexByKey: viewModel.selectableIndexByKey,
                      handleSelect,
                      registerItemRef,
                    }),
                  )}
                </>
              ) : null}

              {viewModel.userSections.map((section) => (
                <React.Fragment key={section.id}>
                  <MentionSubheader disableSticky>{section.title}</MentionSubheader>
                  {section.entries.map((entry) =>
                    renderMentionEntry({
                      entry,
                      selectedIndex,
                      setSelectedIndex,
                      selectableIndexByKey: viewModel.selectableIndexByKey,
                      handleSelect,
                      registerItemRef,
                    }),
                  )}
                </React.Fragment>
              ))}
            </MentionList>
            {showInlineLoading ? (
              <MentionLoadingEmptyState>
                <CircularProgress size={24} />
              </MentionLoadingEmptyState>
            ) : null}
            {!isLoading && !viewModel.selectableEntries.length ? (
              <MentionNoMatchesState>No matches</MentionNoMatchesState>
            ) : null}
            {showOverlayLoading ? (
              <MentionLoadingOverlay>
                <CircularProgress size={24} />
              </MentionLoadingOverlay>
            ) : null}
          </MentionScrollArea>
          <MentionFooter>{FOOTER_HINT_TEXT}</MentionFooter>
        </MentionPaper>
      </ClickAwayListener>
    </Popper>
  );
};

function renderMentionEntry({
  entry,
  selectedIndex,
  setSelectedIndex,
  selectableIndexByKey,
  handleSelect,
  registerItemRef,
}: {
  entry: MentionEntry;
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  selectableIndexByKey: Map<string, number>;
  handleSelect: (option?: MentionOptionItem) => void;
  registerItemRef: (index: number) => (node: HTMLElement | null) => void;
}): JSX.Element {
  const selectableIndex = selectableIndexByKey.get(entry.key);
  const isSelected = selectableIndex === selectedIndex;

  return (
    <MentionEntryButton
      key={entry.key}
      isSelected={isSelected}
      ref={typeof selectableIndex === 'number' ? registerItemRef(selectableIndex) : undefined}
      onClick={() => handleSelect(entry.option)}
      onMouseEnter={() => {
        if (typeof selectableIndex === 'number') {
          setSelectedIndex(selectableIndex);
        }
      }}
    >
      <MentionEntryIcon>
        {entry.type === 'group' ? (
          <Groups2OutlinedIcon fontSize="small" />
        ) : (
          <PersonOutlineIcon fontSize="small" />
        )}
      </MentionEntryIcon>
      <ListItemText
        primary={entry.option.label}
        secondary={entry.type === 'user' ? entry.sectionLabel : undefined}
        primaryTypographyProps={{
          fontSize: '1rem',
          fontWeight: entry.type === 'group' ? 600 : 500,
        }}
        secondaryTypographyProps={{ fontSize: '0.9rem', color: 'text.secondary' }}
      />
      {entry.type === 'group' ? (
        <MentionGroupChip label="Group" size="small" variant="outlined" />
      ) : null}
    </MentionEntryButton>
  );
}

// Kept local after checking shared helpers (`remeda`, `@uidotdev/usehooks`,
// and `apps/portal/src/utils`): no utility matched this mention view-model shaping.
function buildViewModel(items: MentionOptionItem[]): MentionPopupViewModel {
  const groupEntries = items
    .filter((item) => item.entity === GROUP_ENTITY)
    .sort((a, b) => a.label.localeCompare(b.label))
    .map(
      (item, index): MentionEntry => ({
        key: buildEntryKey(item, 'group', index),
        option: item,
        type: 'group',
        sectionLabel: item.assigneeGroupName || item.label,
      }),
    );

  const userSectionsMap = new Map<string, MentionSection>();

  items
    .filter((item) => item.entity === USER_ENTITY)
    .sort((a, b) => {
      const groupCompare = (a.assigneeGroupName || OTHER_USERS_SECTION_TITLE).localeCompare(
        b.assigneeGroupName || OTHER_USERS_SECTION_TITLE,
      );

      if (groupCompare !== 0) return groupCompare;
      return a.label.localeCompare(b.label);
    })
    .forEach((item, index) => {
      const sectionId = item.assigneeGroupId || OTHER_USERS_SECTION_ID;
      const sectionTitle = item.assigneeGroupName || OTHER_USERS_SECTION_TITLE;
      const entry: MentionEntry = {
        key: buildEntryKey(item, 'user', index),
        option: item,
        type: 'user',
        sectionLabel: sectionTitle,
      };

      const existingSection = userSectionsMap.get(sectionId);
      if (existingSection) {
        existingSection.entries.push(entry);
        return;
      }

      userSectionsMap.set(sectionId, {
        id: sectionId,
        title: sectionTitle,
        entries: [entry],
      });
    });

  const userSections = Array.from(userSectionsMap.values()).sort((a, b) =>
    a.title.localeCompare(b.title),
  );
  const selectableEntries = [
    ...groupEntries,
    ...userSections.flatMap((section) => section.entries),
  ];
  const selectableIndexByKey = new Map(
    selectableEntries.map((entry, index) => [entry.key, index]),
  );

  return {
    groupEntries,
    userSections,
    selectableEntries,
    selectableIndexByKey,
  };
}

function buildEntryKey(
  item: MentionOptionItem,
  type: MentionEntryType,
  index: number,
): string {
  return [type, item.entity, item.id, item.assigneeGroupId || 'none', item.label, index].join(
    ':',
  );
}

function normalizeMentionOptions(items: unknown[]): MentionOptionItem[] {
  const normalized: MentionOptionItem[] = [];

  items.forEach((item) => {
    if (!isRecord(item)) return;

    const { id, label, entity } = item;
    if (typeof id !== 'string' || typeof label !== 'string' || typeof entity !== 'string') {
      return;
    }

    normalized.push({
      id,
      label,
      entity,
      assigneeGroupId: toNullableString(item.assigneeGroupId),
      assigneeGroupName: toNullableString(item.assigneeGroupName),
    });
  });

  return normalized;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toNullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}
